<template>
  <div class="request-feedback-modal">
    <b-modal class="cpd-modal" title="Request Feedback" ref="modal" size="lg" okTitle="Send"
             okVariant="info" :okDisabled="!formState" header-bg-variant="info" header-text-variant="light"
             @ok="request" no-close-on-esc>

      <b-form-group label="Reviewer" required>
        <b-tabs pills small v-model="activeEmailSourceIndex">
          <b-tab title="Saved Contact">
            <div class="mt-3">
              <div v-if="loadingContacts" class="text-muted">
                Loading saved contacts...
              </div>

              <div v-else-if="reviewerContacts.length === 0">
                <p class="mb-1">No saved reviewer contacts yet.</p>
              </div>

              <div v-else>
                <b-form-group label="Saved Reviewer" label-for="saved-reviewer-email" required>
                  <b-form-select
                    id="saved-reviewer-email"
                    v-model="selectedContactKey"
                    :options="reviewerContactOptions"
                    :state="savedContactState"
                  />
                </b-form-group>
              </div>
            </div>
          </b-tab>

          <b-tab title="New Contact">
            <div class="mt-3">
              <b-form-group label="Reviewer Name" label-for="reviewer-name" required>
                <b-form-input
                  id="reviewer-name"
                  v-model.trim="manualName"
                  type="text"
                  :state="manualNameState"
                  autocomplete="off"
                />
              </b-form-group>
              <b-form-group label="Reviewer Role / Title" label-for="reviewer-role">
                <b-form-input
                  id="reviewer-role"
                  v-model.trim="manualRole"
                  type="text"
                  autocomplete="off"
                />
              </b-form-group>
              <b-form-group label="Reviewer Email" label-for="reviewer-email" required>
                <b-form-input
                  id="reviewer-email"
                  v-model.trim="manualEmail"
                  type="email"
                  :state="manualEmailState"
                  autocomplete="off"
                />
                <b-form-invalid-feedback :state="manualEmailState">
                  Enter a valid reviewer email address.
                </b-form-invalid-feedback>
              </b-form-group>
              <b-form-group label="Confirm Reviewer Email" label-for="reviewer-email-confirm" required>
                <b-form-input
                  id="reviewer-email-confirm"
                  v-model.trim="manualEmailConfirm"
                  type="email"
                  :state="manualEmailConfirmState"
                  autocomplete="off"
                />
                <b-form-invalid-feedback :state="manualEmailConfirmState">
                  Confirmation email must match Reviewer Email.
                </b-form-invalid-feedback>
              </b-form-group>
              <b-form-group label="Reviewer Phone" label-for="reviewer-phone" required>
                <b-form-input
                  id="reviewer-phone"
                  v-model.trim="manualPhone"
                  type="text"
                  :state="manualPhoneState"
                  autocomplete="off"
                />
                <b-form-invalid-feedback :state="manualPhoneState">
                  Enter a valid phone number.
                </b-form-invalid-feedback>
              </b-form-group>
            </div>
          </b-tab>
        </b-tabs>

        <small class="text-muted">
          You can update your reviewer contacts in
          <a :href="profileSettingsLink" target="_blank" rel="noopener noreferrer" style="text-decoration: underline;">Profile</a>.
        </small>
      </b-form-group>      

      <b-form-group label="Expires in days" label-for="feedback-expiry-days">
        <b-form-input
          id="feedback-expiry-days"
          v-model.number="expiryDays"
          type="number"
          min="0"
          step="1"
          autocomplete="off"
          placeholder="0"
        />
        <small class="text-muted">Leave empty or 0 for never expire.</small>
      </b-form-group>

      <p class="muted">An email will be sent to the reviewer asking them to provide feedback on this CPD entry.</p>

      <h6 class="mt-3">Previous Requests</h6>
      <b-table
        small
        striped
        thead-class="feedback-requests-table__head"
        :items="requestHistory"
        :fields="historyFields"
        :busy="loadingHistory"
        show-empty
      >
        <template #table-busy>
          <div class="text-center text-muted my-2">Loading requests...</div>
        </template>

        <template #empty>
          <div class="text-center text-muted my-2">No feedback requests yet.</div>
        </template>

        <template #cell(expires_at)="data">
          {{ formatExpiry(data.item.expires_at) }}
        </template>

        <template #cell(status)="data">
          <b-badge pill class="status-pill" :class="statusPillClass(data.item.status)">
            {{ formatStatus(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(action)="data">
          <b-button
            v-if="data.item.status === 'pending'"
            size="sm"
            variant="outline-danger"
            :disabled="cancellingRequestId === data.item.id"
            @click="cancelRequest(data.item.id)"
          >
            Cancel
          </b-button>
        </template>
      </b-table>

    </b-modal>
  </div>
</template>

<script>
import Providers from "@/providers";

export default {
  name: "RequestFeedbackModal",
  props: {
    requestCallback: {
      type: Function,
      required: true
    },
    loadRequestsCallback: {
      type: Function,
      required: true
    },
    cancelRequestCallback: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      manualName: '',
      manualEmail: '',
      manualEmailConfirm: '',
      manualRole: '',
      manualPhone: '',
      selectedContactKey: '',
      reviewerContacts: [],
      loadingContacts: false,
      activeEmailSourceIndex: 0,
      entryId: null,
      expiryDays: null,
      loadingHistory: false,
      requestHistory: [],
      cancellingRequestId: null,
      historyFields: [
        { key: 'reviewer_email', label: 'Reviewer Email' },
        { key: 'expires_at', label: 'Expiry' },
        { key: 'status', label: 'Status' },
        { key: 'action', label: 'Action' },
      ],
    };
  },
  methods: {
    reset() {
      this.manualName = '';
      this.manualEmail = '';
      this.manualEmailConfirm = '';
      this.manualRole = '';
      this.manualPhone = '';
      this.selectedContactKey = '';
      this.reviewerContacts = [];
      this.loadingContacts = false;
      this.activeEmailSourceIndex = 0;
      this.expiryDays = null;
    },
    async show(entry) {
      this.reset();
      this.entryId = entry.id;
      this.$refs.modal.show();
      await Promise.all([this.loadReviewerContacts(), this.loadRequestHistory()]);
    },
    loadReviewerContacts() {
      this.loadingContacts = true;

      return Providers.profile
        .getReviewerContacts()
        .then((items) => {
          this.reviewerContacts = Array.isArray(items) ? items : [];
          if (this.reviewerContacts.length === 0) {
            this.activeEmailSourceIndex = 1;
          }
        })
        .catch((resp) => {
          this.reviewerContacts = [];
          this.activeEmailSourceIndex = 1;
          this.$httpError("Failed to load reviewer contacts", resp);
        })
        .finally(() => {
          this.loadingContacts = false;
        });
    },
    async loadRequestHistory() {
      this.loadingHistory = true;
      try {
        const result = await this.loadRequestsCallback(this.entryId);
        this.requestHistory = Array.isArray(result) ? result : [];
      } catch (_) {
        this.requestHistory = [];
      } finally {
        this.loadingHistory = false;
      }
    },
    async request(bvModalEvt) {
      bvModalEvt.preventDefault();
      await this.requestCallback({
        entry_id: this.entryId,
        email: this.activeReviewerEmail,
        reviewer_name: this.activeReviewerName,
        reviewer_role: this.activeReviewerRole || null,
        expiry_days: this.normalizedExpiryDays,
      });
      await this.saveManualReviewerContactIfNeeded();
      this.$refs.modal.hide();
    },
    async saveManualReviewerContactIfNeeded() {
      if (this.activeEmailSourceIndex !== 1) {
        return;
      }

      try {
        await Providers.profile.createReviewerContact({
          full_name: this.manualName.trim(),
          job_title: this.manualRole.trim() || null,
          email: this.manualEmail.trim(),
          phone: this.manualPhone.trim(),
        });
      } catch (resp) {
        // Best effort only: feedback request has already been sent successfully.
        if (resp && resp.status && resp.status !== 422) {
          this.$httpError("Failed to save reviewer contact", resp);
        }
      }
    },
    async cancelRequest(requestId) {
      this.cancellingRequestId = requestId;
      try {
        await this.cancelRequestCallback(requestId);
        await this.loadRequestHistory();
      } finally {
        this.cancellingRequestId = null;
      }
    },
    formatExpiry(value) {
      if (!value) {
        return 'Never';
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleDateString();
    },
    formatStatus(value) {
      if (!value || typeof value !== 'string') {
        return 'Unknown';
      }

      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    statusPillClass(value) {
      if (value === 'submitted') {
        return 'status-pill--submitted';
      }

      if (value === 'cancelled' || value === 'expired') {
        return 'status-pill--warning';
      }

      return 'status-pill--pending';
    }
  },
  computed: {
    selectedContact() {
      return this.reviewerContacts.find((contact) => contact.email === this.selectedContactKey) || null;
    },
    activeReviewerName() {
      if (this.activeEmailSourceIndex === 0) {
        return this.selectedContact ? (this.selectedContact.full_name || '') : '';
      }

      return this.manualName;
    },
    activeReviewerEmail() {
      if (this.activeEmailSourceIndex === 0) {
        return this.selectedContact ? (this.selectedContact.email || '') : '';
      }

      return this.manualEmail;
    },
    activeReviewerRole() {
      if (this.activeEmailSourceIndex === 0) {
        return this.selectedContact ? (this.selectedContact.job_title || '') : '';
      }

      return this.manualRole;
    },
    manualNameState() {
      return this.manualName.trim().length > 0;
    },
    manualEmailState() {
      return this.$utilities.validEmail(this.manualEmail);
    },
    manualEmailConfirmState() {
      return (
        this.$utilities.validEmail(this.manualEmailConfirm) &&
        this.manualEmailConfirm === this.manualEmail
      );
    },
    manualPhoneState() {
      return /^[0-9+\-\s()]+$/.test(this.manualPhone.trim()) && this.manualPhone.trim().length > 0;
    },
    savedContactState() {
      if (!this.selectedContact) {
        return false;
      }

      const name = (this.selectedContact.full_name || '').trim();
      const email = (this.selectedContact.email || '').trim();
      return name.length > 0 && this.$utilities.validEmail(email);
    },
    reviewerContactOptions() {
      return [
        { value: '', text: 'Select saved reviewer' },
        ...this.reviewerContacts.map((contact) => ({
          value: contact.email,
          text: [
            contact.full_name,
            contact.job_title || null,
            contact.email,
            contact.phone || null,
          ].filter(Boolean).join(' - '),
        })),
      ];
    },
    profileSettingsLink() {
      return this.$router.resolve({ name: "profile" }).href;
    },
    normalizedExpiryDays() {
      const parsed = Number(this.expiryDays);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return 0;
      }

      return Math.floor(parsed);
    },
    formState() {
      if (this.activeEmailSourceIndex === 0) {
        return this.savedContactState;
      }

      return this.manualNameState && this.manualEmailState && this.manualEmailConfirmState && this.manualPhoneState;
    }
  }
};
</script>

<style scoped>
.muted {
  color: #6c757d;
}

::v-deep(.feedback-requests-table__head th) {
  color: #fff !important;
}

.status-pill {
  min-width: 90px;
  text-align: center;
}

.status-pill--submitted {
  background-color: #28a745 !important;
  color: #fff !important;
}

.status-pill--warning {
  background-color: #f0ad4e !important;
  color: #212529 !important;
}

.status-pill--pending {
  background-color: #6c757d !important;
  color: #fff !important;
}
</style>
